<template>
  <div class="container">
    <header class="menu" :colspan="cellsPerRow">
      <b>ChordTable</b>
      <div class="clear-btn" @click="clearCells">Clear</div>
    </header>
    <table id="chordTable" ref="chordGrids">
      <tr class="row" v-for="row in rows" :key="row">
        <ChordSymbol v-for="(cell, column) in cells.slice((row - 1) * cellsPerRow, row * cellsPerRow)"
                     :key="cell.uuid"
                     :uuid="cell.uuid"
                     :tabindex="row * cellsPerRow + column"
                     :chord="cell.chord"
                     :config="{...cell.config, selected: selected.has(row * cellsPerRow - cellsPerRow + column)}"
                     @remove_chord="removeChord"
                     @update_selection="updateSelection"
        />
      </tr>
    </table>
  </div>
</template>

<script>

import { v4 } from 'uuid'
import { Chord, RandomChord, chordEqualVoicing, EmptyChord, testUtil } from '@/utils/Chord'
import ChordSymbol from '@/components/ChordSymbol.vue'

export function BuildChordCell (chord = EmptyChord()) {
  return {
    uuid: v4(),
    chord: chord,
    config: {
      selected: false,
      showBass: true,
      showRemoveButton: false,
      showVoicing: true
    }
  }
}

function DefaultCells () {
  const measures = 12
  const meter = 4
  const cells = []
  for (let i = 0; i < measures; i++) {
    for (let j = 0; j < meter; j++) {
      // cells.push(j === 0 ? BuildChordCell(RandomChord()) : BuildChordCell())
      // cells.push(j === 0 ? randomChords[i] : Chord(v4(), '', [i, j]))
      cells.push(new BuildChordCell())
    }
  }
  return cells
}

export default {
  name: 'ChordTable',
  components: {
    ChordSymbol
  },
  setup () {
    return {
      Chord: Chord,
      RandomChord: RandomChord
    }
  },
  props: {
    chordCandidates: {
      default: []
    },
    config: {
      default: {}
    }
  },
  data () {
    return {
      timeSignature: 4,
      measuresPerRow: 4,
      cells: DefaultCells(),
      selected: new Set([0])
    }
  },
  computed: {
    rows () {
      return Math.ceil(this.cells.length / this.cellsPerRow)
    },
    cellsPerRow () {
      return this.measuresPerRow * this.timeSignature
    },
    currentIndex () {
      return Array.from(this.selected)[0]
    },
    previousChordIndex () {
      let index = this.currentIndex - 1
      while (index > 0 && this.cells[index].chord.root === null) {
        index--
      }
      return index
    }
  },
  created () {
    window.addEventListener('keydown', this.onkeydown)
    testUtil()
  },
  methods: {
    stepsToNextMeasure (half = false) {
      if (half) {
        return Math.ceil(Math.round((this.timeSignature / 2 - this.currentIndex % (this.timeSignature / 2)) * 100) / 100)
      } else {
        return this.timeSignature - this.currentIndex % this.timeSignature
      }
    },
    navigate (step) {
      let index = Array.from(this.selected)[0]
      const len = this.cells.length
      index += step
      index += len
      index %= len
      this.selected.clear()
      this.selected.add(index)
    },
    removeSelectedChords () {
      if (this.currentIndex > -1) {
        this.cells[this.currentIndex] = new BuildChordCell()
      }
    },
    removeChord (e) {
      const index = this.cells.findIndex(c => c.uuid === e.uuid)
      if (index) {
        this.cells[index] = new BuildChordCell()
      }
    },
    updateSelection (e) {
      // console.log('uuid is', e.uuid)
      this.cells[this.currentIndex].config.selected = false
      const index = this.cells.findIndex(c => c.uuid === e.uuid)
      this.selected.clear()
      this.selected.add(index)
      this.cells[index].config.selected = true
      // console.log('updated to ', index)
    },
    onkeydown (e) {
      console.info('key')
      // if (e.repeat) {
      //   return
      // }
      // Arrow key navigation
      const index = Array.from(this.selected)[0]
      let steps
      if (/Arrow/.test(e.key) || e.key === 'Tab' || e.key === ' ') {
        switch (e.key) {
          case 'ArrowLeft':
            this.navigate(-this.stepsToNextMeasure(!e.shiftKey))
            break
          case ' ':
            this.navigate(1)
            e.preventDefault()
            break
          case 'ArrowUp':
            this.navigate(-this.cellsPerRow)
            break
          case 'ArrowDown':
            this.navigate(this.cellsPerRow)
            break
          case 'ArrowRight':
            this.navigate(this.stepsToNextMeasure(!!e.shiftKey))
            break
          case 'Tab':
            steps = this.timeSignature - index % this.timeSignature
            if (e.shiftKey) {
              steps *= -1
            }
            this.navigate(steps)
        }
        return
      }

      // Delete key
      if (e.key === 'Delete') {
        this.removeSelectedChords()
        return
      }
      if (e.key === 'Backspace') {
        if (!this.cells[this.currentIndex].chord.empty()) {
          this.removeSelectedChords()
        }
        if (this.previousChordIndex > -1) {
          this.navigate(this.previousChordIndex - this.currentIndex)
        }
        return
      }
      if (e.key === 'Enter' || e.key === 'Return') {
        if (this.chordCandidates.length > 0) {
          this.inputChord({ chord: this.chordCandidates[0] })
          this.navigate(this.stepsToNextMeasure())
        }
      }
    },
    inputChord (e) {
      const chord = e.chord
      if (!chord) {
        console.warn('empty chord!', chord)
      }
      const useBass = !!this.config.useBass
      for (const index of this.selected) {
        this.cells[index] = BuildChordCell(chord)
        this.cells[index].uuid = v4()
        if (!useBass) {
          this.cells[index].chord.bass = null
        }
      }
    },
    inputChordTriggeredByKeyboard (holdNotes) {
      const minPitch = Math.min(...holdNotes)
      const halfLength = minPitch > this.config.pitchSplittingPoint
      const chord = this.chordCandidates[0]
      const useBass = !!this.config.useBass
      const currentIndex = this.currentIndex
      const prevChord = this.previousChordIndex > -1 ? this.cells[this.previousChordIndex].chord : null
      if (!!prevChord && chordEqualVoicing(chord, prevChord)) {
        this.cells[currentIndex] = new BuildChordCell()
      } else {
        this.cells[currentIndex] = new BuildChordCell(chord)
        if (!useBass) {
          this.cells[currentIndex].chord.bass = null
        }
      }
      this.navigate(this.stepsToNextMeasure(halfLength))
    },
    clearCells () {
      for (let i = 0; i < this.cells.length; i++) {
        if (!this.cells[i].chord.empty()) {
          this.cells[i] = BuildChordCell()
        }
      }
      this.selected.clear()
      this.selected.add(0)
    },
    exportChords () {
      const result = []
      for (let i = 0; i < this.cells.length; i++) {
        const chord = this.cells[i].chord
        if (!chord.empty()) {
          const measure = Math.floor(i / this.timeSignature)
          const meter = i % this.timeSignature
          result.push({
            position: [measure, meter],
            chord: chord
          })
        }
      }
      console.log('exported to clipboard')
      navigator.clipboard.writeText(JSON.stringify(result))
    }
  }
}

</script>

<style scoped>
#chordTable {
  display: table;
  /*border: 3px dashed red;*/
  /*background: #fafafa;*/
  background: #e3e3e3;
  width: 100%;
  border-collapse: separate;
  border-spacing: 3px;
  position: relative;
  table-layout: fixed;
  z-index: 0;
  justify-self: flex-start;
}

.container {
  display: flex;
  flex-direction: column;
}

.row {
  line-height: 35px !important;
  height: 35px;
  min-height: 35px;
}

.clear-btn {
  margin-left: 10px;
  user-select: none;
}

.clear-btn:hover {
  background: #fafafa;
  cursor: pointer;
}
</style>
